import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';
import {
  FaTimes,
  FaUserEdit,
  FaGlobe,
  FaCheckSquare,
  FaSquare,
  FaExclamationTriangle,
  FaEye,
  FaMapMarkerAlt,
  FaInfoCircle,
} from 'react-icons/fa';

export default function EditUserModal({ isOpen, user, onClose, onUserUpdated, readOnly }) {
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.usertype === 0;
  // Only Super Admin (0) and District (5) can manage users. Range (6) and Zone (7) are strictly view-only.
  const isReadOnly = readOnly || ![0, 5].includes(currentUser?.usertype);

  const [name, setName] = useState('');
  const [mobileNo, setMobileNo] = useState('');
  const [alterMobileNo, setAlterMobileNo] = useState('');
  const [emailId, setEmailId] = useState('');
  const [isActive, setIsActive] = useState(1);

  const [websitesList, setWebsitesList] = useState([]);
  const [selectedWebsites, setSelectedWebsites] = useState({});

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen || !user) return;

    setName(user.name || '');
    setMobileNo(user.mobileNo || '');
    setAlterMobileNo(user.alterMobileNo || '');
    setEmailId(user.emailId || '');
    setIsActive(user.is_active !== undefined ? user.is_active : 1);

    // Fetch websites — only needed if admin can manage them
    if (isAdmin && !isReadOnly) {
      const loadUserWebsites = async () => {
        try {
          const res = await api.get(`/users/${user.userid}/websites`);
          if (res.data.success) {
            setWebsitesList(res.data.websites || []);
            const mapping = {};
            (res.data.websites || []).forEach((w) => {
              mapping[w.id] = Boolean(w.can_access);
            });
            setSelectedWebsites(mapping);
          }
        } catch (err) {
          console.error('Failed to fetch user website permissions:', err);
        }
      };
      loadUserWebsites();
    }
  }, [isOpen, user, isAdmin, isReadOnly]);

  if (!isOpen || !user) return null;

  const toggleWebsite = (websiteId) => {
    if (isReadOnly) return;
    setSelectedWebsites((prev) => ({
      ...prev,
      [websiteId]: !prev[websiteId],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isReadOnly) {
      onClose();
      return;
    }
    setError('');

    if (!name.trim()) {
      setError('Name cannot be empty.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        mobileNo: mobileNo.trim() || null,
        alterMobileNo: alterMobileNo.trim() || null,
        emailId: emailId.trim() || null,
        is_active: isActive ? 1 : 0,
      };

      // Only include websites in payload when admin is editing
      if (isAdmin) {
        payload.websites = Object.keys(selectedWebsites)
          .filter((id) => selectedWebsites[id])
          .map(Number);
      }

      const res = await api.put(`/users/${user.userid}`, payload);
      if (res.data.success) {
        onUserUpdated();
        onClose();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-container"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '580px' }}
      >
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div
              style={{
                width: '36px',
                height: '36px',
                borderRadius: 'var(--radius-md)',
                background: isReadOnly ? 'rgba(99, 102, 241, 0.2)' : 'rgba(14, 165, 233, 0.2)',
                color: isReadOnly ? 'var(--primary-light, #818cf8)' : 'var(--secondary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {isReadOnly ? <FaEye /> : <FaUserEdit />}
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h3 style={{ fontSize: '1.2rem', margin: 0 }}>
                  {isReadOnly ? 'User Account Details' : `Edit User${isAdmin ? ' & Website Access' : ''}`}
                </h3>
                {isReadOnly && (
                  <span
                    className="badge badge-secondary"
                    style={{
                      fontSize: '0.72rem',
                      padding: '2px 8px',
                      background: 'rgba(255, 255, 255, 0.08)',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                    }}
                  >
                    Read-Only View
                  </span>
                )}
              </div>
              <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                Account: <strong style={{ color: 'var(--text-main)' }}>@{user.username}</strong> ({user.role_label})
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-muted)',
              cursor: 'pointer',
              fontSize: '1.2rem',
            }}
          >
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div style={{ minWidth: '520px' }}>
              {isReadOnly && (
                <div
                  style={{
                    padding: '10px 14px',
                    borderRadius: 'var(--radius-md)',
                    background: '#f0fdf4',
                    border: '1px solid #86efac',
                    color: '#166534',
                    fontSize: '0.83rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '16px',
                  }}
                >
                  <FaInfoCircle style={{ flexShrink: 0, fontSize: '1rem' }} />
                  <span>
                    Range (Type 6) and Zone (Type 7) accounts have view-only access to subordinate accounts. Only District (Type 5) and Super Admin (Type 0) can modify user details.
                  </span>
                </div>
              )}

              {error && (
                <div
                  style={{
                    padding: '10px 14px',
                    borderRadius: 'var(--radius-md)',
                    background: '#fef2f2',
                    border: '1px solid #fca5a5',
                    color: '#991b1b',
                    fontSize: '0.85rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '16px',
                  }}
                >
                  <FaExclamationTriangle />
                  <span>{error}</span>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Full Name / Office Title *</label>
                <input
                  type="text"
                  className="form-control"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={isReadOnly}
                  required
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div className="form-group">
                  <label className="form-label">Primary Mobile</label>
                  <input
                    type="text"
                    maxLength={10}
                    className="form-control"
                    value={mobileNo}
                    onChange={(e) => setMobileNo(e.target.value)}
                    disabled={isReadOnly}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Alternate Mobile</label>
                  <input
                    type="text"
                    maxLength={10}
                    className="form-control"
                    value={alterMobileNo}
                    onChange={(e) => setAlterMobileNo(e.target.value)}
                    disabled={isReadOnly}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Email Address</label>
                <input
                  type="email"
                  className="form-control"
                  value={emailId}
                  onChange={(e) => setEmailId(e.target.value)}
                  disabled={isReadOnly}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div className="form-group">
                  <label className="form-label">Account Status</label>
                  <select
                    className="form-control"
                    value={isActive}
                    onChange={(e) => setIsActive(Number(e.target.value))}
                    disabled={isReadOnly}
                  >
                    <option value={1}>Active (Allowed to Login)</option>
                    <option value={0}>Inactive (Access Suspended)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Assigned Jurisdiction</label>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '10px 12px',
                      borderRadius: 'var(--radius-md)',
                      background: 'rgba(255, 255, 255, 0.04)',
                      border: '1px solid var(--border-color)',
                      fontSize: '0.84rem',
                      color: 'var(--text-main)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <FaMapMarkerAlt style={{ color: 'var(--primary-light, #818cf8)', flexShrink: 0 }} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {user.location_name || 'Global / State'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Authorized Websites — editable by Super Admin, view-only badges for others */}
              {isAdmin && !isReadOnly ? (
                <div
                  style={{
                    padding: '14px',
                    background: '#f8fafc',
                    borderRadius: 'var(--radius-md)',
                    marginTop: '10px',
                    border: '1px solid var(--border-color)',
                  }}
                >
                  <div
                    style={{
                      fontSize: '0.82rem',
                      fontWeight: 600,
                      color: 'var(--primary)',
                      marginBottom: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    <FaGlobe />
                    <span>Manage Website Permissions (user_website_access)</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    {websitesList.map((site) => {
                      const isChecked = Boolean(selectedWebsites[site.id]);
                      return (
                        <div
                          key={site.id}
                          onClick={() => toggleWebsite(site.id)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '8px 12px',
                            borderRadius: 'var(--radius-sm)',
                            background: isChecked ? '#eff6ff' : '#ffffff',
                            border: `1px solid ${isChecked ? '#93c5fd' : '#e2e8f0'}`,
                            cursor: 'pointer',
                            transition: 'all 0.15s ease',
                          }}
                        >
                          <span style={{ color: isChecked ? 'var(--primary)' : 'var(--text-dim)', fontSize: '1.1rem' }}>
                            {isChecked ? <FaCheckSquare /> : <FaSquare />}
                          </span>
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-main)' }}>
                              {site.website_name}
                            </span>
                            <span style={{ fontSize: '0.68rem', color: 'var(--text-dim)' }}>
                              Code: {site.website_code}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    padding: '14px',
                    background: '#f8fafc',
                    borderRadius: 'var(--radius-md)',
                    marginTop: '10px',
                    border: '1px solid var(--border-color)',
                  }}
                >
                  <div
                    style={{
                      fontSize: '0.82rem',
                      fontWeight: 600,
                      color: 'var(--primary)',
                      marginBottom: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    <FaGlobe />
                    <span>Authorized Websites</span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {user.websites && user.websites.length > 0 ? (
                      user.websites.map((site) => (
                        <span
                          key={site.id}
                          style={{
                            fontSize: '0.78rem',
                            padding: '4px 10px',
                            borderRadius: '4px',
                            background: '#eff6ff',
                            color: 'var(--primary)',
                            border: '1px solid #bfdbfe',
                            fontWeight: 500,
                          }}
                        >
                          {site.name} ({site.code})
                        </span>
                      ))
                    ) : (
                      <span style={{ fontSize: '0.78rem', color: 'var(--text-dim)' }}>
                        No websites assigned
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer">
            {isReadOnly ? (
              <button type="button" className="btn btn-primary" onClick={onClose} style={{ minWidth: '100px' }}>
                Close
              </button>
            ) : (
              <>
                <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Updating...' : isAdmin ? 'Save User & Access' : 'Save Changes'}
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
